/**
 * Sovereign Ash: Nareth Protocol
 * Main Entry Point
 *
 * Boot sequence: renderer detection → settings → test arena
 */

import { detectRenderer, getRecommendedPreset } from '@sovereign-ash/rendering';
import { getSettingsManager } from '@sovereign-ash/game-core';
import { TestArena } from './scenes/test-arena';

// ============================================================================
// Boot Sequence
// ============================================================================

async function boot(): Promise<void> {
  const loadingBar = document.getElementById('loading-bar-fill');
  const loadingScreen = document.getElementById('loading-screen');
  const debugOverlay = document.getElementById('debug-overlay');

  const setProgress = (pct: number) => {
    if (loadingBar) loadingBar.style.width = `${pct}%`;
  };

  try {
    // Phase 1: Detect renderer capabilities
    setProgress(10);
    console.log('[Boot] Detecting renderer capabilities...');
    const capabilities = await detectRenderer();
    console.log('[Boot] Renderer:', capabilities);

    setProgress(30);

    // Phase 2: Load settings
    console.log('[Boot] Loading settings...');
    const settings = getSettingsManager();
    const graphicsSettings = settings.getSection('graphics');
    console.log('[Boot] Settings loaded:', graphicsSettings);

    setProgress(50);

    // Phase 3: Apply recommended preset if not custom
    if (graphicsSettings.preset !== 'custom') {
      const recommended = getRecommendedPreset(capabilities.deviceTier);
      console.log('[Boot] Recommended preset for tier:', capabilities.deviceTier, recommended);
    }

    setProgress(70);

    // Phase 4: Initialize test arena
    console.log('[Boot] Initializing test arena...');
    const canvas = document.getElementById('render-canvas') as HTMLCanvasElement;
    if (!canvas) {
      throw new Error('Canvas element not found');
    }

    const arena = new TestArena(canvas, capabilities);
    await arena.initialize();

    setProgress(100);

    // Phase 5: Hide loading screen and start
    setTimeout(() => {
      loadingScreen?.classList.add('hidden');
      arena.start();
      console.log('[Boot] Test arena started. Press F3 for debug overlay.');
    }, 500);

    // Debug overlay toggle (F3)
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F3') {
        e.preventDefault();
        debugOverlay?.classList.toggle('visible');
      }
    });

    // Update debug overlay
    setInterval(() => {
      if (debugOverlay?.classList.contains('visible')) {
        const metrics = arena.getMetrics();
        debugOverlay.innerHTML = `
          <div>FPS: ${metrics.fps.toFixed(1)}</div>
          <div>Backend: ${capabilities.backend}</div>
          <div>Tier: ${capabilities.deviceTier}</div>
          <div>Tick: ${metrics.tick}</div>
          <div>Sim Time: ${metrics.simTime.toFixed(2)}s</div>
          <div>Entities: ${metrics.entityCount}</div>
          <div>Position: ${metrics.position}</div>
        `;
      }
    }, 100);

  } catch (error) {
    console.error('[Boot] Fatal error:', error);
    if (loadingScreen) {
      loadingScreen.innerHTML = `
        <h1 style="color: #ff6b6b;">Initialization Failed</h1>
        <div class="subtitle" style="color: #f0ede8; margin-top: 1rem;">
          ${error instanceof Error ? error.message : 'Unknown error'}
        </div>
        <div class="subtitle" style="margin-top: 2rem;">
          Please ensure you're using a modern browser with hardware acceleration enabled.
        </div>
      `;
    }
  }
}

// Start boot sequence when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
